import { useState, useEffect, useCallback } from 'react';

const useSelection = () => {
  const [selection, setSelection] = useState(null);
  const [selectionRect, setSelectionRect] = useState(null);

  const getSelectedContent = useCallback(() => {
    const windowSelection = window.getSelection();
    
    if (!windowSelection || windowSelection.rangeCount === 0) {
      return null;
    }

    const range = windowSelection.getRangeAt(0);
    const selectedText = windowSelection.toString().trim();
    
    // Check if selection contains images
    const images = [];
    const imageElements = range.cloneContents().querySelectorAll('img');
    imageElements.forEach(img => {
      images.push({
        src: img.src,
        alt: img.alt || '',
        width: img.width,
        height: img.height
      });
    });

    // Get bounding rect for positioning the button
    const rect = range.getBoundingClientRect();

    return {
      text: selectedText,
      images: images,
      html: range.cloneContents(),
      rect: {
        top: rect.top + window.scrollY,
        right: rect.right + window.scrollX,
        bottom: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        height: rect.height
      }
    };
  }, []);

  const updateSelectionState = useCallback(() => {
    const content = getSelectedContent();
    
    if (content && (content.text.length > 0 || content.images.length > 0)) {
      setSelection(content);
      setSelectionRect(content.rect);
    } else {
      setSelection(null);
      setSelectionRect(null);
    }
  }, [getSelectedContent]);

  const clearSelection = useCallback(() => {
    const windowSelection = window.getSelection();
    if (windowSelection) {
      windowSelection.removeAllRanges();
    }
    setSelection(null);
    setSelectionRect(null);
  }, []);

  useEffect(() => {
    document.addEventListener('selectionchange', updateSelectionState);
    document.addEventListener('mouseup', updateSelectionState);
    document.addEventListener('keyup', updateSelectionState);

    return () => {
      document.removeEventListener('selectionchange', updateSelectionState);
      document.removeEventListener('mouseup', updateSelectionState);
      document.removeEventListener('keyup', updateSelectionState);
    };
  }, [updateSelectionState]);

  useEffect(() => {
    if (!selection) {
      return;
    }

    const handleScrollOrResize = () => {
      const content = getSelectedContent();
      if (content && (content.text.length > 0 || content.images.length > 0)) {
        setSelection(prev => prev ? { ...prev, rect: content.rect } : content);
        setSelectionRect(content.rect);
      } else {
        setSelection(null);
        setSelectionRect(null);
      }
    };

    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [selection, getSelectedContent]);

  return {
    selection,
    selectionRect,
    clearSelection
  };
};

export default useSelection;

